'use client'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'
import { useCallback, useEffect, useRef } from 'react'
import tocbot from 'tocbot'
import { useScript } from 'usehooks-ts'

interface FooterRendererProps {
  footerHtml: string
}

export default function PageFooter({ footerHtml }: FooterRendererProps) {
  const footerRef = useRef<HTMLDivElement>(null)
  const scriptsExecutedRef = useRef(false)

  // Sử dụng useScript hook để load jQuery
  const jqueryStatus = useScript(
    'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.3.1/jquery.min.js',
    {
      removeOnUnmount: false,
    }
  )

  useEffect(() => {
    const tocifyCSS = document.createElement('link')
    tocifyCSS.rel = 'stylesheet'
    tocifyCSS.href =
      'https://cdnjs.cloudflare.com/ajax/libs/jquery.tocify/1.9.0/stylesheets/jquery.tocify.min.css'
    tocifyCSS.crossOrigin = 'anonymous'
    tocifyCSS.referrerPolicy = 'no-referrer'
    document.head.appendChild(tocifyCSS)
  }, [])

  const executeScripts = useCallback(() => {
    if (scriptsExecutedRef.current || !footerRef.current) return
    scriptsExecutedRef.current = true

    const scripts = footerRef.current.querySelectorAll('script')
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script')

      // copy all attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value)
      })

      // copy script content
      newScript.textContent = oldScript.textContent

      // replace old script with new to trigger execution
      oldScript.parentNode?.replaceChild(newScript, oldScript)
    })

    // trigger DOMContentLoaded manually
    setTimeout(() => {
      document.dispatchEvent(new Event('DOMContentLoaded'))
    }, 300)
  }, [])

  useEffect(() => {
    scriptsExecutedRef.current = false

    // Delay TOC init until DOM is rendered
    requestAnimationFrame(() => {
      const tocEl = document.querySelector('.toc-bot')
      const hasHeadings = document.querySelector('.rich-html h2, .rich-html h3')

      if (tocEl && hasHeadings) {
        const headings = document.querySelectorAll(
          '.rich-html h2, .rich-html h3'
        )
        headings.forEach((el, index) => {
          if (!el.id) {
            el.id = `toc-heading-${index}`
          }
        })
        tocbot.init({
          tocSelector: '.toc-bot',
          contentSelector: '.rich-html',
          headingSelector: 'h2, h3',
          collapseDepth: 6,
          scrollSmooth: true,
          scrollSmoothOffset: 150,
        })
      }
    })

    return () => tocbot.destroy()
  }, [footerHtml])

  useEffect(() => {
    if (jqueryStatus === 'ready') {
      const win = window
      if (win.jQuery && !win.$) {
        win.$ = win.jQuery
      }
      executeScripts()
    }
  }, [jqueryStatus, executeScripts])

  return (
    <>
      {jqueryStatus === 'ready' && (
        <div ref={footerRef} dangerouslySetInnerHTML={{ __html: footerHtml }} />
      )}
    </>
  )
}
